import { err, ok } from '@src/services/common/Result';
import { isTempClipId } from '@src/services/WriteQueueService';
import { extractErrorInfo } from '@src/utility/error';

import {
  checkLoadedVideoAgainstHead,
  parentChanged,
  pickRecordFields,
  recordFieldsToPatch,
} from './loadedVideoFreshness';

import type { LoadedVideo, RecordFields } from './loadedVideoFreshness';
import type { Result } from '@src/services/common/Result';
import type { VideoVersion } from '@src/types.ts/story';

export interface ReconcileLoadedVideoDeps {
  getLoadedVideo(): LoadedVideo | undefined;
  /** The loaded clip is the head of its own version list. */
  isLoadedVideoTheLatest(): boolean;
  isRendering(videoId: string): boolean;
  /** Loads the version list without syncing the loaded clip; returns its head. */
  loadVersionList(videoId: string): Promise<VideoVersion | undefined>;
}

export type ReconcileOutcome =
  | { kind: 'skipped' | 'current' }
  | { kind: 'fieldsChanged'; video: LoadedVideo; patch: RecordFields }
  | { kind: 'contentChanged'; head: VideoVersion };

/**
 * PLAT-11911 — decides how a clip Creator Studio opened without loading it
 * (seeded or kept) is brought up to Dato's head: record-field changes are
 * patched in, a content change is reported. The caller applies the outcome.
 */
export class ReconcileLoadedVideoService {
  constructor(private readonly _deps: ReconcileLoadedVideoDeps) {}

  async reconcile(): Promise<Result<ReconcileOutcome>> {
    const deps = this._deps;
    const loaded = deps.getLoadedVideo();
    // A temp clip has no Dato record yet, only a synthetic version list.
    if (!loaded?.id || isTempClipId(loaded.id)) return ok({ kind: 'skipped' });
    // An older version opened from the version panel stays as picked.
    if (!deps.isLoadedVideoTheLatest()) return ok({ kind: 'skipped' });
    // A render rewrites the record when it lands; check after that.
    if (deps.isRendering(loaded.id)) return ok({ kind: 'skipped' });

    const { versionId, resourcesMetadata } = loaded;
    const before = pickRecordFields(loaded);
    let head: VideoVersion | undefined;
    try {
      head = await deps.loadVersionList(loaded.id);
    } catch (e) {
      return err('EXTERNAL', 'Version list failed to load', {
        videoId: loaded.id,
        ...extractErrorInfo(e),
      });
    }
    // A save or a clip switch while fetching: this head may predate it.
    if (
      !head ||
      deps.getLoadedVideo() !== loaded ||
      loaded.versionId !== versionId ||
      loaded.resourcesMetadata !== resourcesMetadata
    ) {
      return ok({ kind: 'skipped' });
    }

    switch (checkLoadedVideoAgainstHead(loaded, head)) {
      case 'current':
        if (
          loaded.parentVideo === before.parentVideo &&
          parentChanged(loaded.parentVideo, head.parentVideo)
        ) {
          return ok({
            kind: 'fieldsChanged',
            video: loaded,
            patch: { parentVideo: head.parentVideo },
          });
        }
        return ok({ kind: 'current' });
      case 'fieldsChanged':
        return ok({
          kind: 'fieldsChanged',
          video: loaded,
          patch: recordFieldsToPatch(loaded, before, head),
        });
      case 'contentChanged':
        return ok({ kind: 'contentChanged', head });
    }
  }
}
